import { StoreQueryType } from "@common/utils/use-query/types";
import { purchasesStore } from "@stores/purchases";
import { STRIPE_CHARGE_STATUS_PENDING } from "@stores/purchases/constants";
import { IStripeChargeStatus } from "@stores/purchases/types";
import { useEffect, useState } from "react";

interface IUseCheckPurchasePaymentStatusProps {
  id: string;
  check: boolean;
}

export function useCheckPurchasePaymentStatus({
  id,
  check = false,
}: IUseCheckPurchasePaymentStatusProps): Pick<
  StoreQueryType<IStripeChargeStatus>,
  "isLoading" | "error" | "data"
> {
  const {
    error,
    data: purchase,
    refetch: refetchPurchase,
  } = purchasesStore.useGetPurchase(id);

  const [isLoading, setIsLoading] = useState(false);

  const [stripeChargeStatus, setStripeChargeStatus] =
    useState<IStripeChargeStatus>();

  // 3. purchase
  useEffect(() => {
    const lastStripeCharge = purchase?.stripe_charges?.length
      ? purchase.stripe_charges[purchase?.stripe_charges.length - 1]
      : undefined;

    // If a purchase has no Stripe charge
    // it is considered in progress
    const newStripeChargeStatus =
      lastStripeCharge?.stripe_charge_status || STRIPE_CHARGE_STATUS_PENDING;
    if (newStripeChargeStatus !== stripeChargeStatus) {
      setStripeChargeStatus(newStripeChargeStatus);
    }
  }, [purchase, stripeChargeStatus]);

  // 2. checking
  useEffect(() => {
    let timeout;
    if (check && stripeChargeStatus === STRIPE_CHARGE_STATUS_PENDING) {
      timeout = setInterval(refetchPurchase, 5000);
    }

    return () => clearInterval(timeout);
  }, [check, stripeChargeStatus, refetchPurchase]);

  // 1. start/stop checking
  useEffect(() => {
    if (check) {
      setIsLoading(true);
      setStripeChargeStatus(STRIPE_CHARGE_STATUS_PENDING);
    } else {
      setIsLoading(false);
    }
  }, [check]);

  return {
    isLoading,
    error,
    data: stripeChargeStatus,
  };
}
